/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { ClickStatus } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import LinkButton from 'bundles/authentication-common/components/LinkButton';
import ForgotPasswordForm from 'bundles/authentication-common/components/forms/ForgotPasswordForm';
import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication-common/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
  hideBackToLink?: boolean;
};

export type State = {
  isSent: boolean;
  requestMagicLink: boolean;
};

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: 0;
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-50);

    .cds-text-field-root {
      margin-top: var(--cds-spacing-50) !important;
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300) var(--cds-spacing-200);
    }
  `,
  description: css`
    margin: var(--cds-spacing-50) 0 0;
  `,
  descriptionSent: css`
    margin: var(--cds-spacing-300) 0 0;

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-50) 0 0;
    }
  `,
  navigation: css`
    margin: var(--cds-spacing-200) 0 0;
    display: flex;
  `,
  navigationSent: css`
    margin-top: 40px;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  header: css`
    margin-bottom: var(--cds-spacing-100);

    ${breakpoints.down('xs')} {
      margin-bottom: 0;
    }
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    margin: 0;
  `,
};

const ForgotPassword = (props: Props) => {
  const { className, hideBackToLink = false, navigate, magicLinkRedirect } = props;
  const [isSent, setIsSent] = useState(false);
  const [requestMagicLink, setRequestMagicLink] = useState(false);
  const trackEvent = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_modal', {
    modal: {
      name: 'reset_password_email_sent',
    },
  });

  const handleTracking = (status: ClickStatus) => {
    trackEvent('click_button', {
      button: {
        name: 'reset_password_email_request_form_submit',
        clickStatus: status,
      },
      modal: { name: 'forgot_password' },
    });
  };

  const navigateToLogin = () => {
    trackEvent('click_button', {
      button: { name: 'back_to_login_form' },
      modal: { name: 'forgot_password' },
    });
    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  const navigateToMagicLink = (email: string) => {
    navigate(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email });
  };

  const resetPassword = ({ email }: { email: string }) => {
    if (!email) {
      handleTracking('invalid');
      return;
    }

    handleTracking('valid');
    api.post('api/password/v1/requestReset', { data: { email } });
    setIsSent(true);
  };

  const sendMagicLink = ({ email }: { email: string }) => {
    trackEvent('click_button', {
      button: { name: 'email_me_login_link' },
      modal: { name: 'forgot_password' },
    });
    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });
    navigateToMagicLink(email);
  };

  const requestMagicLinkHandler = () => {
    setRequestMagicLink(true);
  };

  return (
    <section ref={isSent ? visibilityTrackingRef : null} css={[styles.container, className]} aria-live="polite">
      <header css={[styles.section, styles.header]}>
        <Typography2 component="h1" variant="titleXSmall" css={styles.title} align="left">
          {isSent ? _t('Email has been sent') : _t('Forgot password')}
        </Typography2>
        <Typography2
          component="p"
          variant="bodyPrimary"
          color="supportText"
          css={[styles.description, isSent && styles.descriptionSent]}
          align="left"
        >
          {isSent ? (
            <TrackedDiv trackingName="reset_request_sent" withVisibilityTracking={true}>
              {_t(
                "Please check your email inbox for a password recovery link. Don't forget to check your spam folder."
              )}
            </TrackedDiv>
          ) : (
            _t("Enter the email address you use on Coursera. We'll send you a link to reset your password.")
          )}
        </Typography2>
      </header>

      {!isSent && (
        <ForgotPasswordForm
          onSubmit={requestMagicLink ? sendMagicLink : resetPassword}
          onRequestMagicLink={requestMagicLinkHandler}
          submitCTALabel={_t('Reset Password')}
          submitCTATrackingName="reset_page_button"
        />
      )}
      {!hideBackToLink && (
        <Typography2
          component="p"
          variant="bodySecondary"
          css={[styles.navigation, isSent && styles.navigationSent, styles.section]}
          align="left"
        >
          <FormattedMessage
            message={_t('Back to {loginLink}')}
            loginLink={<LinkButton onClick={navigateToLogin}>{_t('Log in')}</LinkButton>}
          />
        </Typography2>
      )}
    </section>
  );
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'reset_password_page',
  },
}))(ForgotPassword);
